/* <cv-offer> — Curvana bundle OfferSelector.
   - Tier radio-cards (1 / 2 / 3 pairs), keyboard accessible.
   - Selected tier reveals per-pair Color/Size pickers populated from the real product options.
   - Add to cart builds one line item per pair via /cart/add.js (quantity-based, real variant price).
   - Advertised bundle totals are made real by auto-applying a percentage discount code
     (min-quantity gated) on the way to /cart — invisible to the customer.
*/
class CurvanaOffer extends HTMLElement {
  connectedCallback() {
    this.variants = this._json('.cv-offer__variants') || [];
    this.cartUrl = this.dataset.cartUrl || '/cart';
    // Trimmed: codes pasted into the theme editor often carry stray whitespace,
    // which would survive encodeURIComponent and 404 the /discount/ route.
    this.discount2 = (this.dataset.discount2 || '').trim();
    this.discount3 = (this.dataset.discount3 || '').trim();
    this.defaultVariant = this.dataset.defaultVariant || '';
    this.hasOptions = this.dataset.hasOptions === 'true';
    this.tiers = Array.from(this.querySelectorAll('.cv-offer__tier'));

    this.tiers.forEach((tier) => {
      tier.addEventListener('click', () => this.select(tier));
      tier.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.select(tier);
        }
      });
    });

    this.addEventListener('click', (e) => {
      if (e.target.closest('.cv-offer__submit')) {
        e.preventDefault();
        this.addToCart(e.target.closest('.cv-offer__submit'));
      }
    });

    this._initColorPicks();

    // Sticky ATC (a separate section) asks us to add via this document event.
    document.addEventListener('curvana:add-to-cart', () => this.addToCart());
    // Sticky ATC also asks us to reveal the color/size pickers of the selected bundle.
    document.addEventListener('curvana:focus-options', () => this.focusOptions());

    const active = this.tiers.find((t) => t.classList.contains('is-active')) || this.tiers[0];
    if (active) this.select(active);
  }

  _json(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Wire up the custom image-based color dropdowns (one per pair).
  _initColorPicks() {
    this.colorWidgets = Array.from(this.querySelectorAll('.cv-colorpick'));
    this.colorWidgets.forEach((w) => {
      const trigger = w.querySelector('.cv-colorpick__trigger');
      const menu = w.querySelector('.cv-colorpick__menu');
      const input = w.querySelector('[data-role="color"]');
      const nameEl = w.querySelector('[data-role="color-name"]');
      const thumb = w.querySelector('.cv-colorpick__trigger .cv-colorpick__thumb');
      if (!trigger || !menu) return;

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleColor(w, !w.classList.contains('is-open'));
      });

      menu.querySelectorAll('.cv-colorpick__opt').forEach((opt) => {
        opt.addEventListener('click', (e) => {
          e.stopPropagation();
          const val = opt.dataset.value || '';
          if (input) input.value = val;
          if (nameEl) nameEl.textContent = opt.querySelector('.cv-colorpick__name')?.textContent || val;
          if (thumb) {
            const img = opt.querySelector('img');
            thumb.innerHTML = img ? '<img src="' + img.getAttribute('src') + '" alt="" width="200" height="200">' : '';
          }
          menu.querySelectorAll('.cv-colorpick__opt').forEach((o) => {
            const on = o === opt;
            o.classList.toggle('is-selected', on);
            o.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          this._toggleColor(w, false);
          trigger.focus();
        });
      });
    });

    // One document listener closes any open color dropdown on an outside click / Escape.
    document.addEventListener('click', () => this._closeAllColors());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._closeAllColors();
    });
  }

  _toggleColor(widget, open) {
    if (open) this._closeAllColors();
    widget.classList.toggle('is-open', open);
    // Lift the whole tier card so the menu isn't painted over by later tiers.
    widget.closest('.cv-offer__tier')?.classList.toggle('is-picking', open);
    const trigger = widget.querySelector('.cv-colorpick__trigger');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  _closeAllColors() {
    (this.colorWidgets || []).forEach((w) => {
      w.classList.remove('is-open');
      w.closest('.cv-offer__tier')?.classList.remove('is-picking');
      const t = w.querySelector('.cv-colorpick__trigger');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  select(tier) {
    this.tiers.forEach((t) => {
      const on = t === tier;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-checked', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    const price = tier.dataset.price || '';
    const label = tier.dataset.label || '';
    // Update every submit button label ("Add To Cart — $64.95").
    this.querySelectorAll('.cv-offer__submit').forEach((btn) => {
      btn.textContent = price ? 'Add To Cart — ' + price : 'Add To Cart';
    });
    // Mirror the selection into any sticky ATC.
    document.querySelectorAll('[data-cv-atc-title]').forEach((el) => {
      el.textContent = (label ? label : '') + (label && price ? ' · ' : '') + (price || '');
    });
  }

  // Scroll the selected bundle's Color/Size pickers into view and focus the first one.
  focusOptions() {
    const tier = this.tiers.find((t) => t.classList.contains('is-active')) || this.tiers[0];
    if (!tier) return;
    const picks = tier.querySelector('.cv-offer__picks');
    const target = picks || tier;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstField = tier.querySelector('.cv-offer__pick .cv-colorpick__trigger, .cv-offer__pick select');
    if (firstField) {
      // Focus after the smooth scroll settles so the field is in view.
      setTimeout(() => firstField.focus({ preventScroll: true }), 400);
    }
  }

  _resolve(pick) {
    const colorSel = pick.querySelector('[data-role="color"]');
    const sizeSel = pick.querySelector('[data-role="size"]');
    const color = colorSel ? colorSel.value : null;
    const size = sizeSel ? sizeSel.value : null;
    const match = this.variants.find(
      (v) => (color == null || v.color === color) && (size == null || v.size === size)
    );
    return match ? match : null;
  }

  async addToCart(btn) {
    const tier = this.tiers.find((t) => t.classList.contains('is-active')) || this.tiers[0];
    if (!tier) return;
    const qty = parseInt(tier.dataset.qty, 10) || 1;
    const picks = Array.from(tier.querySelectorAll('.cv-offer__pick'));
    const items = [];

    if (this.hasOptions && this.variants.length && picks.length) {
      for (const pick of picks) {
        const match = this._resolve(pick);
        if (!match || !match.available) {
          this._flash(btn, match ? 'Sold out — pick another' : 'Choose color & size');
          pick.querySelector('select')?.focus();
          return;
        }
        items.push({ id: match.id, quantity: 1 });
      }
    } else if (this.defaultVariant) {
      items.push({ id: Number(this.defaultVariant), quantity: qty });
    } else {
      this._flash(btn, 'Unavailable');
      return;
    }

    const buttons = this.querySelectorAll('.cv-offer__submit');
    buttons.forEach((b) => (b.disabled = true));

    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!resp.ok) throw new Error('add failed');

      let target = this.cartUrl;
      const redirect = encodeURIComponent(this.cartUrl);
      if (qty >= 3 && this.discount3) {
        target = '/discount/' + encodeURIComponent(this.discount3) + '?redirect=' + redirect;
      } else if (qty === 2 && this.discount2) {
        target = '/discount/' + encodeURIComponent(this.discount2) + '?redirect=' + redirect;
      }
      window.location.assign(target);
    } catch (err) {
      buttons.forEach((b) => (b.disabled = false));
      this._flash(btn, 'Try again');
    }
  }

  _flash(btn, msg) {
    const target = btn || this.querySelector('.cv-offer__submit');
    if (!target) return;
    const original = target.textContent;
    target.textContent = msg;
    setTimeout(() => { target.textContent = original; }, 1800);
  }
}
customElements.define('cv-offer', CurvanaOffer);
